import { useEffect, useRef, useState } from "react";
import {
  ScanBarcode,
  Search,
  Tag,
  PackageX,
  RotateCcw,
  Zap,
  ShieldCheck,
  EyeOff,
  Clock,
  Hash,
  FolderOpen,
  BadgeCheck,
  Package,
  Keyboard,
  TimerReset,
  ShieldCheck as SecureIcon,
} from "lucide-react";
import { useProducts, usePromotions, ProductRecord, PRODUCT_UNIT_LABEL } from "@/api/inventory";
import { apiClient } from "@/api/client";
import { resolvePromotion, discountPerUnit } from "@/utils/pricing";
import { ProductImage } from "@/components/common/ProductImage";
import type { ApiSuccessResponse } from "@erp/shared";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

type Tab = "scan" | "search";

const INFO_CARDS = [
  { icon: Zap, title: "Consulta rápida", text: "Escanea el código o busca por nombre y ve el precio en segundos." },
  { icon: ShieldCheck, title: "Información confiable", text: "Precio y promociones en vivo, directo del catálogo de la tienda." },
  { icon: EyeOff, title: "Sin registro de venta", text: "Consultar aquí no descuenta inventario ni genera ninguna venta." },
  { icon: Clock, title: "Disponible 24/7", text: "Pantalla lista para usarse todo el día, sin necesidad de un cajero." },
];

const tabButtonClass = (active: boolean) =>
  `flex items-center gap-1.5 border-b-2 pb-2 text-sm font-medium transition-colors ${
    active ? "border-brand-600 text-brand-700" : "border-transparent text-gray-400 hover:text-gray-600"
  }`;

/** Segundos que el precio queda en pantalla antes de volver solo a esperar la siguiente consulta (pantalla de cara al cliente, sin cajero). */
const AUTO_RESET_SECONDS = 12;

/**
 * Validador de precios: escanear/buscar un producto y mostrar SOLO su
 * precio (con la promoción vigente aplicada, si tiene) — no vende, no toca
 * inventario ni caja. Pensado para una pantalla/lector fijo en el pasillo,
 * de cara al cliente. Reutiliza exactamente el mismo endpoint de búsqueda,
 * la misma lógica de promoción vigente (src/utils/pricing.ts) y el mismo
 * componente de foto (ProductImage) que el resto del sistema — no duplica
 * catálogo ni lógica de precios.
 */
export default function PriceCheckPage() {
  const [tab, setTab] = useState<Tab>("scan");
  const [scanCode, setScanCode] = useState("");
  const [nameQuery, setNameQuery] = useState("");
  const [scanning, setScanning] = useState(false);
  const [found, setFound] = useState<ProductRecord | null | undefined>(undefined); // undefined = aún no se buscó nada
  const [secondsLeft, setSecondsLeft] = useState(AUTO_RESET_SECONDS);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { data: promotions } = usePromotions(true);

  // Autocompletado en vivo mientras se escribe en "Buscar por nombre".
  const { data: liveMatches } = useProducts({ search: nameQuery.length >= 2 ? nameQuery : undefined, isActive: true, pageSize: 8 });

  useEffect(() => {
    if (found !== undefined) return;
    if (tab === "scan") scanInputRef.current?.focus();
    else nameInputRef.current?.focus();
  }, [tab, found]);

  // Pantalla de cara al cliente: tras mostrar el resultado (encontrado o no),
  // vuelve sola a esperar la siguiente consulta pasados AUTO_RESET_SECONDS —
  // así no depende de que alguien pulse "Nueva búsqueda" entre un cliente y otro.
  useEffect(() => {
    if (found === undefined) return;
    setSecondsLeft(AUTO_RESET_SECONDS);
    const tick = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tick);
          reset();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [found]);

  const handleScanSubmit = async () => {
    const value = scanCode.trim();
    if (!value) return;
    setScanning(true);
    try {
      const { data } = await apiClient.get<ApiSuccessResponse<ProductRecord[]>>("/inventory/products", {
        params: { search: value, isActive: true, pageSize: 5 },
      });
      const results = data.data ?? [];
      const exact = results.find((p) => p.barcode === value || p.sku === value) ?? (results.length === 1 ? results[0] : undefined);
      setFound(exact ?? null);
    } finally {
      setScanning(false);
    }
  };

  const selectProduct = (product: ProductRecord) => {
    setFound(product);
    setNameQuery("");
    setScanCode("");
  };

  const reset = () => {
    setFound(undefined);
    setScanCode("");
    setNameQuery("");
    setTab("scan");
    setTimeout(() => scanInputRef.current?.focus(), 0);
  };

  const goToSearchTab = () => {
    setFound(undefined);
    setScanCode("");
    setTab("search");
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const promotion = found ? resolvePromotion(found, promotions) : undefined;
  const unitPrice = found ? Number(found.salePrice) : 0;
  const discount = found ? discountPerUnit(found, promotion) : 0;
  const finalPrice = Math.max(0, unitPrice - discount);
  const inStock = found ? Number(found.stockQty) > 0 : false;

  return (
    <div className={`mx-auto space-y-4 transition-all ${found === undefined ? "max-w-2xl" : "max-w-full"}`}>
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <ScanBarcode size={18} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Validador de precios</h1>
          <p className="text-sm text-gray-500">Escanea o busca un producto para consultar su precio. Esto no registra ninguna venta.</p>
        </div>
      </div>

      {/* Estado inicial: hero con ilustración, pestañas, buscador y franja informativa */}
      {found === undefined && (
        <div className="card overflow-hidden p-0">
          <div className="grid grid-cols-1 gap-6 p-6 sm:p-8 md:grid-cols-[160px_1fr]">
            <div className="hidden items-center justify-center rounded-xl bg-gradient-to-br from-violet-50 to-brand-50 md:flex">
              <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl bg-white shadow-sm">
                <ScanBarcode size={40} className="text-violet-500" />
                <span className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-violet-500 text-white shadow-sm">
                  <Tag size={14} />
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex gap-6 border-b border-gray-200">
                <button
                  type="button"
                  className={tabButtonClass(tab === "scan")}
                  onClick={() => {
                    setNameQuery("");
                    setTab("scan");
                  }}
                >
                  <ScanBarcode size={15} /> Escanear código
                </button>
                <button
                  type="button"
                  className={tabButtonClass(tab === "search")}
                  onClick={() => {
                    setScanCode("");
                    setTab("search");
                  }}
                >
                  <Search size={15} /> Buscar por nombre
                </button>
              </div>

              {tab === "scan" ? (
                <div className="flex items-center gap-2">
                  <div className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 focus-within:border-brand-300 focus-within:bg-white">
                    <ScanBarcode size={18} className="shrink-0 text-gray-400" />
                    <input
                      ref={scanInputRef}
                      className="flex-1 border-0 bg-transparent p-0 text-sm focus:ring-0"
                      placeholder="Escanear código de barras, o escribir SKU..."
                      value={scanCode}
                      disabled={scanning}
                      autoFocus
                      onChange={(e) => setScanCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleScanSubmit();
                        }
                      }}
                    />
                    <Keyboard size={15} className="shrink-0 text-gray-300" />
                  </div>
                  <button type="button" className="btn-primary shrink-0 px-4" disabled={scanning || !scanCode.trim()} onClick={handleScanSubmit}>
                    <Search size={15} className="mr-1.5" /> Buscar
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 focus-within:border-brand-300 focus-within:bg-white">
                      <Search size={18} className="shrink-0 text-gray-400" />
                      <input
                        ref={nameInputRef}
                        className="flex-1 border-0 bg-transparent p-0 text-sm focus:ring-0"
                        placeholder="Escribe el nombre del producto..."
                        value={nameQuery}
                        autoFocus
                        onChange={(e) => setNameQuery(e.target.value)}
                      />
                      <Keyboard size={15} className="shrink-0 text-gray-300" />
                    </div>
                    <button
                      type="button"
                      className="btn-primary shrink-0 px-4"
                      disabled={!liveMatches?.data?.length}
                      onClick={() => {
                        if (liveMatches?.data?.[0]) selectProduct(liveMatches.data[0]);
                      }}
                    >
                      <Search size={15} className="mr-1.5" /> Buscar
                    </button>
                  </div>
                  {nameQuery.length >= 2 && (liveMatches?.data?.length ?? 0) > 0 && (
                    <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100">
                      {liveMatches!.data.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-gray-50"
                          onClick={() => selectProduct(p)}
                        >
                          <ProductImage productId={p.id} hasImage={p.hasImage} alt={p.name} className="h-9 w-9" iconSize={14} />
                          <span className="flex-1 text-gray-900">
                            {p.name} <span className="ml-1 text-xs text-gray-400">{p.sku}</span>
                          </span>
                          <span className="font-medium text-gray-700">{currency.format(Number(p.salePrice))}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {nameQuery.length >= 2 && liveMatches && liveMatches.data.length === 0 && (
                    <p className="px-1 text-sm text-gray-400">No se encontraron productos con ese nombre.</p>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-400">También puedes buscar por nombre, SKU o código de barras del producto.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 divide-x divide-y divide-gray-100 border-t border-gray-100 sm:grid-cols-4 sm:divide-y-0">
            {INFO_CARDS.map((c) => (
              <div key={c.title} className="flex items-start gap-2.5 p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
                  <c.icon size={16} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-900">{c.title}</p>
                  <p className="text-[11px] leading-snug text-gray-500">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Barra compacta de búsqueda + temporizador de reinicio automático, una vez que ya se hizo una consulta */}
      {found !== undefined && (
        <div className="flex items-center gap-3 rounded-lg border border-brand-200 bg-brand-50/50 px-4 py-2.5">
          <ScanBarcode size={18} className="shrink-0 text-brand-600" />
          <span className="flex-1 truncate text-sm text-gray-700">{found ? found.barcode || found.sku : scanCode || nameQuery}</span>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium text-brand-700 shadow-sm">
            <TimerReset size={13} className="text-brand-500" />
            Nueva consulta en {secondsLeft}s
          </span>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={reset} aria-label="Limpiar">
            <PackageX size={15} />
          </button>
          <button type="button" className="btn-primary shrink-0 px-3 py-1.5 text-xs" onClick={reset}>
            <RotateCcw size={13} className="mr-1" /> Nueva búsqueda
          </button>
        </div>
      )}

      {/* Estado: no encontrado — a pantalla completa para que se lea desde lejos */}
      {found === null && (
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 rounded-2xl border border-gray-100 bg-white p-10 text-center shadow-sm">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-100 text-gray-400">
            <PackageX size={44} />
          </div>
          <div>
            <p className="text-2xl font-semibold text-gray-700 sm:text-3xl">No encontramos ese producto</p>
            <p className="mt-1 text-base text-gray-400">Verifica el código o intenta buscarlo por nombre.</p>
          </div>
          <button type="button" className="btn-secondary text-base" onClick={goToSearchTab}>
            <Search size={18} className="mr-2" /> Buscar por nombre
          </button>
        </div>
      )}

      {/* Estado: producto encontrado — ocupa toda la pantalla disponible, con tipografía grande para leerse desde lejos */}
      {found && (
        <div className="grid min-h-[75vh] grid-cols-1 items-center gap-8 rounded-2xl border border-gray-100 bg-white p-8 shadow-sm sm:p-12 lg:grid-cols-[auto_1fr_auto] lg:gap-14 lg:p-16">
          <div className="flex justify-center lg:justify-start">
            <ProductImage
              productId={found.id}
              hasImage={found.hasImage}
              alt={found.name}
              className="h-56 w-56 sm:h-72 sm:w-72 lg:h-80 lg:w-80"
              iconSize={64}
            />
          </div>

          <div className="space-y-4 text-center lg:text-left">
            <span className="inline-flex items-center rounded-full bg-brand-50 px-3 py-1 text-sm font-semibold uppercase tracking-wide text-brand-700">
              Producto
            </span>
            <p className="text-3xl font-semibold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">{found.name}</p>

            <div className="grid grid-cols-1 gap-4 pt-2 sm:grid-cols-2">
              <div className="flex items-start justify-center gap-2.5 lg:justify-start">
                <Hash size={20} className="mt-0.5 shrink-0 text-brand-500" />
                <div className="text-left">
                  <p className="text-xs uppercase tracking-wide text-gray-400">SKU / Código</p>
                  <p className="text-lg text-gray-700">{found.barcode || found.sku}</p>
                </div>
              </div>
              <div className="flex items-start justify-center gap-2.5 lg:justify-start">
                <FolderOpen size={20} className="mt-0.5 shrink-0 text-brand-500" />
                <div className="text-left">
                  <p className="text-xs uppercase tracking-wide text-gray-400">Categoría</p>
                  <p className="text-lg text-gray-700">{found.category?.name ?? "Sin categoría"}</p>
                </div>
              </div>
              <div className="flex items-start justify-center gap-2.5 lg:justify-start">
                <BadgeCheck size={20} className="mt-0.5 shrink-0 text-brand-500" />
                <div className="text-left">
                  <p className="text-xs uppercase tracking-wide text-gray-400">Marca</p>
                  <p className="text-lg text-gray-700">{found.brand || "—"}</p>
                </div>
              </div>
              <div className="flex items-start justify-center gap-2.5 lg:justify-start">
                <Package size={20} className="mt-0.5 shrink-0 text-brand-500" />
                <div className="text-left">
                  <p className="text-xs uppercase tracking-wide text-gray-400">Presentación</p>
                  <p className="text-lg text-gray-700">{found.presentation || PRODUCT_UNIT_LABEL[found.unit]}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl bg-green-50/60 px-10 py-10 text-center lg:min-w-[300px]">
            <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-semibold uppercase tracking-wide text-green-700">
              Precio de venta
            </span>
            {promotion && <p className="text-lg text-gray-400 line-through">{currency.format(unitPrice)}</p>}
            <p className="flex items-baseline gap-1.5 text-7xl font-bold text-green-600 sm:text-8xl lg:text-9xl">
              {currency.format(promotion ? finalPrice : unitPrice)}
              <span className="text-xl font-medium text-green-500">COP</span>
            </p>
            {promotion && (
              <p className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-4 py-1.5 text-base font-medium text-green-700">
                <Tag size={16} /> {promotion.name}
              </p>
            )}
            <span
              className={`mt-1 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-lg font-medium ${
                inStock ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}
            >
              <span className={`h-2.5 w-2.5 rounded-full ${inStock ? "bg-green-500" : "bg-red-500"}`} />
              {inStock ? "En stock" : "Agotado"}
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-1.5 pt-2 text-xs text-gray-400">
        <SecureIcon size={13} /> Sistema seguro y autorizado
      </div>
    </div>
  );
}
